export type InboundSiteLine<TGroupId extends string = string> = Readonly<{
  groupId: TGroupId;
  calledNumber: string;
}>;

export type InboundSiteDefinition<TGroupId extends string = string> = Readonly<{
  id: string;
  inboundLines: readonly InboundSiteLine<TGroupId>[];
}>;

const E164_PHONE_NUMBER_PATTERN = /^\+[1-9]\d{1,14}$/;

/**
 * Defines a read-only inbound-line registry and validates its routing keys eagerly.
 *
 * Called numbers are globally unique because one phone number cannot safely identify two sites,
 * even when the sites belong to different groups. Resolution also checks the group so a live line
 * cannot be reused accidentally for a sandbox call.
 */
export function defineInboundSiteRegistry<const TSite extends InboundSiteDefinition>(sites: readonly TSite[]) {
  const siteIds = new Set<string>();
  const routeByCalledNumber = new Map<string, { groupId: string; site: TSite }>();

  for (const site of sites) {
    if (!site.id.trim()) throw new Error("Inbound site registry contains an empty site ID");
    if (siteIds.has(site.id)) throw new Error(`Inbound site registry contains duplicate site ID ${site.id}`);
    if (site.inboundLines.length === 0) {
      throw new Error(`Inbound site registry site ${site.id} has no inbound lines`);
    }

    siteIds.add(site.id);

    for (const line of site.inboundLines) {
      if (!E164_PHONE_NUMBER_PATTERN.test(line.calledNumber)) {
        throw new Error(`Inbound site registry contains invalid E.164 number ${line.calledNumber}`);
      }

      if (routeByCalledNumber.has(line.calledNumber)) {
        throw new Error(`Inbound site registry contains duplicate called number ${line.calledNumber}`);
      }

      routeByCalledNumber.set(line.calledNumber, { groupId: line.groupId, site });
    }
  }

  function resolve(groupId: string, calledNumber: string): TSite | null {
    const route = routeByCalledNumber.get(calledNumber);

    return route && route.groupId === groupId ? route.site : null;
  }

  function requireSite(groupId: string, calledNumber: string): TSite {
    const site = resolve(groupId, calledNumber);

    if (!site) {
      throw new Error(`No inbound site mapping for group ${groupId} and phone number ${calledNumber}`);
    }

    return site;
  }

  return { sites, resolve, require: requireSite } as const;
}
